import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Download, FileText, Edit, Award, Calendar, Loader2 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { toast } from "sonner";
import API from "../api/api01";

export function Examination() {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const userFromStorage = JSON.parse(localStorage.getItem("user") || "{}");
  const studentId = userFromStorage?.id;

  useEffect(() => {
    if (studentId) {
      fetchExamData();
    } else {
      setLoading(false);
    }
  }, [studentId]);

  const fetchExamData = async () => {
    try {
      const response = await API.get(`/superadmin/student/exam-data/${studentId}`);
      setData(response.data);
    } catch (error) {
      console.error("Error fetching exam data:", error);
      toast.error("Failed to load examination details");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (type: string) => {
    toast.success(`${type} download started`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const results = data?.results || [];
  const upcomingExams = data?.upcomingExams || [];
  const hallTicket = data?.hallTicket;
  const overallCgpa = data?.overall_cgpa || "0.0";

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1>Exams & Grading</h1>
        <p className="text-muted-foreground text-sm mt-1">
          View results, hall tickets, and manage your examination details
        </p>
      </div>

      <Tabs defaultValue="results" className="space-y-6">
        <TabsList className="grid w-full grid-cols-2 lg:grid-cols-3">
          <TabsTrigger value="results">Results</TabsTrigger>
          <TabsTrigger value="hallticket">Hall Ticket</TabsTrigger>
          <TabsTrigger value="rtrv">RT-RV Form</TabsTrigger>
        </TabsList>

        {/* Results Tab */}
        <TabsContent value="results" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Academic Results</CardTitle>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Overall CGPA</p>
                  <p className="text-2xl font-semibold text-primary">{overallCgpa}</p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {results.length === 0 ? (
                <div className="text-center py-10 text-muted-foreground border border-dashed rounded-lg">
                  No academic results found yet.
                </div>
              ) : (
                results.map((semester: any, index: number) => (
                  <div key={index} className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold">{semester.semester}</h4>
                      <Badge className="bg-primary/10 text-primary hover:bg-primary/20">
                        SGPA: {semester.sgpa}
                      </Badge>
                    </div>
                    <div className="space-y-2">
                      {semester.subjects.map((subject: any, subIndex: number) => (
                        <div
                          key={subIndex}
                          className="flex items-center justify-between p-3 rounded-lg border border-border"
                        >
                          <div className="flex-1">
                            <p className="text-sm font-medium">{subject.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {subject.credits} Credits
                            </p>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="text-sm text-muted-foreground">
                              {subject.score}%
                            </span>
                            <Badge className="bg-secondary/10 text-secondary hover:bg-secondary/20 min-w-[50px] justify-center">
                              {subject.grade}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Hall Ticket Tab */}
        <TabsContent value="hallticket" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Examination Hall Ticket</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {hallTicket ? (
                <div className="p-4 rounded-lg border-2 border-dashed border-border">
                  <div className="text-center space-y-3">
                    <Award className="h-12 w-12 text-muted-foreground mx-auto" />
                    <div>
                      <h4>{hallTicket.exam_name}</h4>
                      <p className="text-sm text-muted-foreground mt-1">{hallTicket.date}</p>
                    </div>
                    <Button
                      onClick={() => handleDownload("Hall Ticket")}
                      className="gap-2"
                    >
                      <Download className="h-4 w-4" />
                      Download Hall Ticket
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-10 text-muted-foreground border border-dashed rounded-lg">
                  No hall ticket available for download at this time.
                </div>
              )}

              <div className="space-y-3">
                <h4 className="text-sm font-semibold">Upcoming Examinations</h4>
                {upcomingExams.length === 0 ? (
                  <div className="text-sm text-muted-foreground italic">No exams scheduled currently.</div>
                ) : (
                  upcomingExams.map((exam: any, index: number) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 rounded-lg bg-muted/30"
                    >
                      <div>
                        <p className="text-sm font-medium">{exam.subject}</p>
                        <p className="text-xs text-muted-foreground flex items-center gap-3 mt-1">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {exam.date}
                          </span>
                          <span>{exam.time}</span>
                          <span>{exam.room}</span>
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* RT-RV Form Tab */}
        <TabsContent value="rtrv" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Recounting & Revaluation</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 rounded-lg bg-muted/30 space-y-3">
                <div className="flex items-start gap-3">
                  <FileText className="h-5 w-5 text-primary mt-0.5" />
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold">Apply for RT/RV</h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      Request recounting or revaluation for your exam papers. Last date: Nov 30, 2025
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm">
                    <Edit className="h-3.5 w-3.5 mr-2" />
                    Apply for Recounting
                  </Button>
                  <Button variant="outline" size="sm">
                    <Edit className="h-3.5 w-3.5 mr-2" />
                    Apply for Revaluation
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <h4 className="text-sm font-semibold">Application History</h4>
                <div className="text-sm text-muted-foreground text-center py-8 border border-dashed rounded-lg">
                  No previous applications
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
